import { useEffect, useState } from 'react';
import { Loader2, Bell, AlertTriangle, Info, Megaphone, X, Calendar, MapPin, ShieldAlert } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API = import.meta.env.VITE_API_URL || '/api/v1';

interface Comunicado {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  criadoEm: string;
  escola?: { id: string; nome: string };
  municipio?: { id: string; nome: string };
  lido?: boolean;
}

export function Comunicados() {
  const [lista, setLista] = useState<Comunicado[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Comunicado | null>(null);
  
  const token = localStorage.getItem('ac_token');
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    async function load() {
      try {
        const responsavelId = user?.responsavelId || user?.id;
        if (!responsavelId) {
          setLoading(false);
          return;
        }
        
        const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/comunicados`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (res.ok) {
          const data = await res.json();
          setLista(Array.isArray(data) ? data : data.data || []);
        }
      } catch (err) {
        console.error('[MobileComunicados] Erro ao carregar:', err);
      }
      setLoading(false);
    }
    load();
  }, [token, user?.id, user?.responsavelId]);

  const handleSelectComunicado = (c: Comunicado) => {
    setSelected(c);
    const responsavelId = user?.responsavelId || user?.id;
    if (responsavelId && !c.lido) {
      setLista((prev) =>
        prev.map((item) => (item.id === c.id ? { ...item, lido: true } : item)),
      );
      fetch(
        `${API}/mobile/responsaveis/${responsavelId}/comunicados/${c.id}/visualizar`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      ).catch(() => {});
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50dvh' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
      </div>
    );
  }

  const getIcon = (tipo: string) => {
    if (tipo === 'URGENTE') return <AlertTriangle size={18} />;
    if (tipo === 'GERAL' || tipo === 'AVISO') return <Megaphone size={18} />;
    return <Info size={18} />;
  };

  const getColor = (tipo: string) => {
    if (tipo === 'URGENTE') return { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' };
    if (tipo === 'EVENTO') return { bg: '#dbeafe', color: '#2563eb', border: '#bfdbfe' };
    return { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' };
  };

  return (
    <div style={{ padding: '16px 4px', fontFamily: 'Inter, system-ui, sans-serif' }} className="m-animate">
      <p className="m-section-title" style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 16px' }}>
        Comunicados Oficiais
      </p>

      {lista.length === 0 ? (
        <div className="m-empty" style={{ padding: '60px 20px', textAlign: 'center', background: '#f8fafc', borderRadius: 20, border: '1px dashed #e2e8f0' }}>
          <div className="m-empty-icon" style={{ width: 56, height: 56, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <Bell size={24} style={{ color: '#7c3aed' }} />
          </div>
          <p style={{ fontWeight: 700, color: '#1e293b', margin: '0 0 4px', fontSize: '0.95rem' }}>Nenhum comunicado ativo</p>
          <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>Você será notificado imediatamente quando novos avisos forem publicados.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {lista.map((c) => {
            const cor = getColor(c.tipo);
            const autor = c.municipio ? `Prefeito (${c.municipio.nome})` : c.escola ? `Diretor (${c.escola.nome})` : 'Administração';
            const isMuni = !!c.municipio;
            const dataFormatada = new Date(c.criadoEm).toLocaleDateString('pt-BR');

            return (
              <div 
                key={c.id} 
                onClick={() => handleSelectComunicado(c)}
                style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  padding: 16,
                  border: !c.lido ? '1.5px solid #7c3aed' : isMuni ? '1.5px solid #ca8a04' : '1px solid #e2e8f0',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'transform 0.15s ease',
                }}
              >
                {/* Tag de origem em cima */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: isMuni ? '#fef9c3' : '#f3e8ff',
                      color: isMuni ? '#a16207' : '#6b21a8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      {isMuni ? <MapPin size={10} /> : <AlertTriangle size={10} />}
                      {isMuni ? 'MUNICIPAL' : 'ESCOLA'}
                    </span>
                    {!c.lido && (
                      <span style={{
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        padding: '2px 6px',
                        borderRadius: 6,
                        background: '#7c3aed',
                        color: '#ffffff',
                      }}>
                        NOVO
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
                    {dataFormatada}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: 38, height: 38, borderRadius: 10, 
                    background: cor.bg, color: cor.color, border: `1px solid ${cor.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                  }}>
                    {getIcon(c.tipo)}
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontSize: '0.92rem', fontWeight: 800, color: '#1e293b', margin: '0 0 4px', lineHeight: 1.3 }}>
                      {c.titulo}
                    </h3>
                    <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0 0 6px', fontWeight: 600 }}>
                      Emitido por: {autor}
                    </p>
                    <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>
                      {c.corpo}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODAL DE DETALHES DO COMUNICADO ── */}
      {selected && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(8px)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          padding: 0,
        }}
        onClick={() => setSelected(null)}
        >
          <div 
            style={{
              background: '#ffffff',
              width: '100%',
              maxHeight: '90dvh',
              borderTopLeftRadius: 30,
              borderTopRightRadius: 30,
              padding: '24px 20px 40px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 -10px 30px rgba(0,0,0,0.15)',
              animation: 'slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle do bottom sheet */}
            <div style={{ width: 40, height: 5, background: '#cbd5e1', borderRadius: 3, margin: '0 auto 16px' }} />
            
            {/* Header com tipo e fechar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                padding: '4px 10px',
                borderRadius: 8,
                background: selected.tipo === 'URGENTE' ? '#fee2e2' : '#f5f3ff',
                color: selected.tipo === 'URGENTE' ? '#dc2626' : '#7c3aed',
                border: selected.tipo === 'URGENTE' ? '1px solid #fca5a5' : '1px solid #ddd6fe',
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }}>
                {selected.tipo === 'URGENTE' ? <ShieldAlert size={12} /> : <Megaphone size={12} />}
                {selected.tipo}
              </span>
              <button 
                onClick={() => setSelected(null)}
                style={{ 
                  border: 'none', background: '#f1f5f9', width: 32, height: 32, 
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  color: '#64748b', cursor: 'pointer' 
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Conteúdo com Scroll */}
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: 4 }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 850, color: '#0f172a', margin: '0 0 12px', lineHeight: 1.3, letterSpacing: '-0.02em' }}>
                {selected.titulo}
              </h2>
              
              <div style={{ 
                display: 'flex', flexDirection: 'column', gap: 6, 
                padding: '12px 14px', background: '#f8fafc', borderRadius: 14, 
                border: '1px solid #e2e8f0', marginBottom: 20, fontSize: '0.8rem', color: '#64748b' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Calendar size={13} />
                  <span>Publicado em: {new Date(selected.criadoEm).toLocaleString('pt-BR')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MapPin size={13} />
                  <span>Origem: {selected.municipio ? `Prefeitura de ${selected.municipio.nome}` : selected.escola ? `Colégio ${selected.escola.nome}` : 'Administração'}</span>
                </div>
              </div>

              {/* Corpo de Texto Principal */}
              <p style={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.6, margin: '0 0 24px', whiteSpace: 'pre-wrap' }}>
                {selected.corpo}
              </p>

              {/* Arte/Foto Decorativa baseada na origem */}
              <div style={{
                background: selected.municipio 
                  ? 'linear-gradient(135deg, #fef08a 0%, #ca8a04 100%)' 
                  : 'linear-gradient(135deg, #ddd6fe 0%, #7c3aed 100%)',
                borderRadius: 18,
                padding: '24px 20px',
                color: selected.municipio ? '#854d0e' : '#ffffff',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 30px rgba(0,0,0,0.05)'
              }}>
                <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.15)', borderRadius: '50%' }} />
                <Megaphone size={40} style={{ margin: '0 auto 10px', opacity: 0.85 }} />
                <h4 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: '0.9rem' }}>
                  {selected.municipio ? 'Comunicado da Prefeitura' : 'Aviso Escolar Oficial'}
                </h4>
                <p style={{ margin: 0, fontSize: '0.74rem', opacity: 0.8 }}>
                  Este comunicado foi distribuído de forma oficial e integrada ao painel dos pais.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
